// React&NextJS
import React, { useContext, useEffect, useState } from 'react'
// Context&Auth
import { UIContext } from '../../../context/UIContext'
// 3rd party libraries
import { AnimatePresence, motion } from 'framer-motion'
import uuid from 'react-uuid'
// Services&Helper functions
import { useKeyDown } from '../../../hooks/useKeyDown'
// Styles
import s from './ShareModal.module.scss'
import classnames from 'classnames'
// Components
import { Container } from '../../core/container/Container'
import { WhiteBox } from '../../core/whiteBox/WhiteBox'
import { Text } from '../../core/text/Text'
import { Input } from '../../core/input/Input'
import {
    EmailIcon,
    EmailShareButton,
    FacebookIcon,
    FacebookShareButton,
    TwitterIcon,
    TwitterShareButton,
} from 'react-share'
import { Memory } from '../../../types/types'
import { Icon } from '../../core/icon/Icon'
import memoryHelperFunctions from '../../../utils/memoryHelperFunctions'

const c = classnames.bind(s)

type Props = {
    isOpen?: boolean
    isLoading?: boolean
    setOpenModal?: Function
    title: string
    memory: Memory
}

type Email = {
    subject?: string
    body?: string
}

const ShareModal = ({
    isOpen,
    setOpenModal,
    title,
    memory,
    isLoading,
}: Props) => {
    const shareID = uuid().substring(0, 4)
    const { uiState, setUIState } = useContext(UIContext)
    const [link, setLink] = useState<string>(
        `https://minningar.is/memories/${memory.mid}?shareID=${shareID}`
    )
    const [email, setEmail] = useState<Email>({
        subject: `Minningarsíða - ${memory.fullName}`,
        body: `Minningarsíða - ${memory.fullName}`,
    })

    // check funeral status
    const funeralStatus = !memoryHelperFunctions.checkIfFuneralFinished(
        new Date(),
        memory
    )

    const funeralText = memory.announcements.funeral?.text
    const announcementText = memory.announcements.deceased?.text
    const greetingText = memory.announcements.greeting?.text

    useEffect(() => {
        if (isOpen) {
            setUIState({ ...uiState, canScroll: false })
        } else {
            setUIState({ ...uiState, canScroll: true })
        }
        return () => {
            setUIState({ ...uiState, canScroll: true })
        }
    }, [isOpen])

    useKeyDown(['Escape'], ({ key }) => {
        if (key === 'Escape' && isOpen) {
            setOpenModal(false)
        }
    })

    const checkIfClosing = (e: React.BaseSyntheticEvent) => {
        if (e.target?.parentNode?.id === 'background') {
            setOpenModal(false)
        }
    }

    const closeModal = () => {
        setLink(
            `https://minningar.is/memories/${memory.mid}?shareID=${shareID}`
        )
        setOpenModal(false)
    }

    if (isOpen) {
        return (
            <AnimatePresence>
                <motion.div
                    className={s.background}
                    onMouseDown={(e) => checkIfClosing(e)}
                    id="background"
                    animate={isOpen ? 'visible' : 'hidden'}
                    initial="hidden"
                    exit="hidden"
                    transition={{
                        duration: 0.2,
                        ease: 'easeOut',
                    }}
                    variants={{
                        visible: {
                            opacity: 1,
                        },
                        hidden: {
                            opacity: 0,
                        },
                    }}
                >
                    <Container className={s.container}>
                        <WhiteBox
                            className={c(s.modal, isLoading && s.loadModal)}
                        >
                            <div
                                className={s.closeBtn}
                                onClick={() => closeModal()}
                            >
                                <Icon variant="close" />
                            </div>
                            {/* only display radio buttons if funeral isn't finished*/}
                            {!isLoading && (
                                <>
                                    {funeralStatus && (
                                        <>
                                            <Text variant="h4">{title}</Text>
                                            <div className={s.radioBtns}>
                                                <Input
                                                    type="radio"
                                                    name="funeral"
                                                    value={`https://minningar.is/memories/${memory.mid}?shareID=${shareID}`}
                                                    label="Deila minningarsíðu"
                                                    onChange={(e) =>
                                                        setLink(e.target.value)
                                                    }
                                                    onClick={() =>
                                                        setEmail({
                                                            subject: `Minningarsíða - ${memory.fullName}`,
                                                            body: `Minningarsíða - ${memory.fullName}`,
                                                        })
                                                    }
                                                />
                                                {announcementText !== '' && (
                                                    <Input
                                                        type="radio"
                                                        name="funeral"
                                                        value={`https://minningar.is/memories/${memory.mid}?shared=announcement&shareID=${shareID}`}
                                                        label="Deila andlátstilkynningu"
                                                        onChange={(e) =>
                                                            setLink(
                                                                e.target.value
                                                            )
                                                        }
                                                        onClick={() =>
                                                            setEmail({
                                                                subject: `Andlátstilkynning - ${memory.fullName}`,
                                                                body: announcementText,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {funeralText !== '' && (
                                                    <Input
                                                        type="radio"
                                                        name="funeral"
                                                        value={`https://minningar.is/memories/${memory.mid}?shared=funeral&shareID=${shareID}`}
                                                        label="Deila útfarartilkynningu"
                                                        onChange={(e) =>
                                                            setLink(
                                                                e.target.value
                                                            )
                                                        }
                                                        onClick={() =>
                                                            setEmail({
                                                                subject: `Útfarartilkynning - ${memory.fullName}`,
                                                                body: funeralText,
                                                            })
                                                        }
                                                    />
                                                )}
                                                {greetingText !== '' && (
                                                    <Input
                                                        type="radio"
                                                        name="funeral"
                                                        value={`https://minningar.is/memories/${memory.mid}?shared=greeting&shareID=${shareID}`}
                                                        label="Deila þakkartilkynningu"
                                                        onChange={(e) =>
                                                            setLink(
                                                                e.target.value
                                                            )
                                                        }
                                                        onClick={() =>
                                                            setEmail({
                                                                subject: `Þakkir - ${memory.fullName}`,
                                                                body: greetingText,
                                                            })
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <Text variant="date">Deila á</Text>
                                    <div className={s.icons}>
                                        <FacebookShareButton
                                            url={link}
                                            onClick={() => closeModal()}
                                        >
                                            <FacebookIcon
                                                size={64}
                                                round={true}
                                            />
                                        </FacebookShareButton>
                                        <TwitterShareButton
                                            url={link}
                                            onClick={() => closeModal()}
                                        >
                                            <TwitterIcon
                                                size={64}
                                                round={true}
                                            />
                                        </TwitterShareButton>
                                        <EmailShareButton
                                            url={link}
                                            subject={email.subject}
                                            body={email.body}
                                            separator={'\n'}
                                        >
                                            <EmailIcon
                                                size={64}
                                                round={true}
                                                onClick={() => closeModal()}
                                            />
                                        </EmailShareButton>
                                    </div>
                                </>
                            )}
                            {isLoading && (
                                <>
                                    <div>
                                        <Text
                                            variant="h4"
                                            align="center"
                                            className={s.loadHeader}
                                        >
                                            Augnablik...
                                        </Text>
                                    </div>
                                    <div className={s.loader}>
                                        <div className={s.loaderInside}></div>
                                    </div>
                                    <div className={s.smallText}>
                                        <Text variant="p-small" align="center">
                                            Verið er að sækja spjöld fyrir
                                            deilingu. Það getur tekið smástund.
                                        </Text>
                                    </div>
                                </>
                            )}
                        </WhiteBox>
                    </Container>
                </motion.div>
            </AnimatePresence>
        )
    }
    return null
}

export default ShareModal
